import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { type FunctionComponent, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useTranslate } from '~/shared/context/use-translate';
import { availableI18nLanguages } from '~/shared/internationalization';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Button } from '~/ui/core/button/Button';
import { Dialog } from '~/ui/core/dialog/Dialog';
import { Input } from '~/ui/core/input/Input';
import { RadioGroup, RadioGroupItem } from '~/ui/core/radio-group/RadioGroup';
import { Select, SelectItem } from '~/ui/core/select/Select';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { DialogActions, DialogForm, FormField, FormLabel } from './styles';
import type { AllowedRoleId, InvitationMethod, InviteTeamMemberFormData } from './types';
import { ALLOWED_ROLES } from './types';

type InviteTeamMemberDialogProps = {
    readonly availableMethods?: readonly InvitationMethod[];
    readonly optionsAreLoading?: boolean;
    readonly onClose: () => void;
    readonly onInvite: (data: InviteTeamMemberFormData) => void;
    readonly open: boolean;
    readonly title?: string;
};

const Content = styled(Dialog.Content)({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '28rem',
    overflow: 'hidden',
    padding: 0,
});

const ScrollableContent = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xl,
    overflowY: 'auto',
    padding: `${theme.spacing['3xl']} ${theme.spacing['3xl']} 0`,
}));

const ActionsWrapper = styled.div(({ theme }) => ({
    padding: theme.spacing['3xl'],
}));

type FormValues = {
    readonly email: string;
    readonly firstName: string;
    readonly language: string;
    readonly lastName: string;
    readonly method: InvitationMethod;
    readonly role: AllowedRoleId;
};

const languageEntries = Object.entries(availableI18nLanguages) as ReadonlyArray<
    readonly [string, { readonly code: string; readonly name: string }]
>;

const useDialogTitle = (title: string | undefined): string => {
    const translate = useTranslate();

    return title ?? translate('pages.company-settings.users-and-roles.invite-dialog.title');
};

export const InviteTeamMemberDialog: FunctionComponent<InviteTeamMemberDialogProps> = ({
    availableMethods,
    optionsAreLoading = false,
    onClose,
    onInvite,
    open,
    title,
}) => {
    const translate = useTranslate();
    const { i18n } = useTranslation();
    const currentLanguage = i18n.resolvedLanguage ?? 'en-US';
    const defaultMethod = availableMethods?.[0] ?? 'EMAIL';
    const dialogTitle = useDialogTitle(title);

    const { control, handleSubmit, reset, setValue, watch } = useForm<FormValues>({
        defaultValues: {
            email: '',
            firstName: '',
            language: currentLanguage,
            lastName: '',
            method: defaultMethod,
            role: 'user',
        },
    });

    const selectedMethod = watch('method');
    const showMethodSelector = availableMethods !== undefined && availableMethods.length > 1;
    const showRoleSelector = selectedMethod !== 'SSO';

    useEffect(() => {
        if (open) {
            const method = availableMethods?.[0];
            if (is.nonEmptyString(method)) {
                setValue('method', method);
            }
        }
    }, [open, availableMethods, setValue]);

    const handleFormSubmit = handleSubmit((data) => {
        onInvite({
            email: data.email,
            firstName: data.firstName,
            inviteLanguage: data.language,
            lastName: data.lastName,
            method: data.method,
            roles: data.method !== 'SSO' ? [data.role] : [],
        });
        reset();
        onClose();
    });

    const handleOpenChange = (isOpen: boolean): void => {
        if (!isOpen) {
            reset();
            onClose();
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <Dialog.Portal>
                <Dialog.DarkBackdrop />
                <Content>
                    <DialogForm className="relative" onSubmit={handleFormSubmit}>
                        {optionsAreLoading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                                <Spinner className="h-6 w-6" />
                            </div>
                        )}
                        <ScrollableContent>
                            <Dialog.Title>{dialogTitle}</Dialog.Title>
                            <FormField>
                                <FormLabel htmlFor="firstName">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.first-name')} *
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="firstName"
                                    render={({ field }) => (
                                        <Input
                                            id="firstName"
                                            name={field.name}
                                            placeholder={translate(
                                                'pages.company-settings.users-and-roles.invite-dialog.first-name-placeholder',
                                            )}
                                            type="text"
                                            value={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                            </FormField>
                            <FormField>
                                <FormLabel htmlFor="lastName">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.last-name')} *
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="lastName"
                                    render={({ field }) => (
                                        <Input
                                            id="lastName"
                                            name={field.name}
                                            placeholder={translate(
                                                'pages.company-settings.users-and-roles.invite-dialog.last-name-placeholder',
                                            )}
                                            type="text"
                                            value={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                            </FormField>
                            <FormField>
                                <FormLabel htmlFor="email">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.email')} *
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="email"
                                    render={({ field }) => (
                                        <Input
                                            id="email"
                                            name={field.name}
                                            placeholder={translate(
                                                'pages.company-settings.users-and-roles.invite-dialog.email-placeholder',
                                            )}
                                            type="email"
                                            value={field.value}
                                            onBlur={field.onBlur}
                                            onChange={field.onChange}
                                        />
                                    )}
                                    rules={{ required: true }}
                                />
                            </FormField>
                            {showMethodSelector && (
                                <FormField>
                                    <FormLabel>
                                        {translate('pages.company-settings.users-and-roles.invite-dialog.method')}
                                    </FormLabel>
                                    <Controller
                                        control={control}
                                        name="method"
                                        render={({ field }) => (
                                            <RadioGroup value={field.value} onValueChange={field.onChange}>
                                                {availableMethods.map((method) => {
                                                    const prefix =
                                                        'pages.company-settings.users-and-roles.invite-dialog';
                                                    const methodKey = `${prefix}.method-${method.toLowerCase()}`;

                                                    return (
                                                        <RadioGroupItem
                                                            key={method}
                                                            label={translate(methodKey as TranslationKey)}
                                                            value={method}
                                                        />
                                                    );
                                                })}
                                            </RadioGroup>
                                        )}
                                    />
                                </FormField>
                            )}
                            {showRoleSelector && (
                                <FormField>
                                    <FormLabel>
                                        {translate('pages.company-settings.users-and-roles.invite-dialog.role')}
                                    </FormLabel>
                                    <Controller
                                        control={control}
                                        name="role"
                                        render={({ field }) => (
                                            <Select
                                                placeholder={translate(
                                                    'pages.company-settings.users-and-roles.invite-dialog.role-placeholder',
                                                )}
                                                value={field.value}
                                                onValueChange={field.onChange}
                                            >
                                                {ALLOWED_ROLES.map((role) => {
                                                    const roleKey = `pages.company-settings.users-and-roles.roles.${role}`;

                                                    return (
                                                        <SelectItem key={role} value={role}>
                                                            {translate(roleKey as TranslationKey)}
                                                        </SelectItem>
                                                    );
                                                })}
                                            </Select>
                                        )}
                                    />
                                </FormField>
                            )}
                            <FormField>
                                <FormLabel>
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.language')}
                                </FormLabel>
                                <Controller
                                    control={control}
                                    name="language"
                                    render={({ field }) => (
                                        <Select value={field.value} onValueChange={field.onChange}>
                                            {languageEntries.map(([key, config]) => (
                                                <SelectItem key={key} value={config.code}>
                                                    {config.name}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormField>
                        </ScrollableContent>
                        <ActionsWrapper>
                            <DialogActions>
                                <Button size="sm" type="button" variant="ghost" onClick={onClose}>
                                    {translate('common.cancel')}
                                </Button>
                                <Button disabled={optionsAreLoading} size="sm" type="submit" variant="default">
                                    {translate('pages.company-settings.users-and-roles.invite-dialog.invite-button')}
                                </Button>
                            </DialogActions>
                        </ActionsWrapper>
                    </DialogForm>
                </Content>
            </Dialog.Portal>
        </Dialog>
    );
};
